import React, { useEffect, useState } from 'react';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { ColumnDef } from '../../../atoms/Table/tableFeatures';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import SearchLineIcon from 'remixicon-react/SearchLineIcon';
import CheckLineIcon from 'remixicon-react/CheckLineIcon';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import ArrowRightLineIcon from 'remixicon-react/ArrowRightLineIcon';
import TimeLineIcon from 'remixicon-react/TimeLineIcon';
import ShieldCheckLineIcon from 'remixicon-react/ShieldCheckLineIcon';
import BankLineIcon from 'remixicon-react/BankLineIcon';
import PriceTag2LineIcon from 'remixicon-react/PriceTag2LineIcon';
import Spinner from '../../../atoms/Spinner/Spinner';
import { Modal } from '../../../atoms/Modal/Modal';
import MerchantPricesModal from './Prices/MerchantPricesModal';
import { useIsFlagEnabledForOrg } from '../../../../services/helpers/isFlagEnabled';
import Card from '../../../atoms/Card/Card';
import CardBody from '../../../atoms/Card/CardBody';
import Table from '../../../atoms/Table/Table';
import Pill from '../../../atoms/Status/Pill';
import Flex from '../../../atoms/Box/Flex';
import FlagIcon from '../../../atoms/FlagIcon/FlagIcon';
import EmptyState from '../../../atoms/Information/EmptyState';
import DecoratorInput from '../../../atoms/Input/DecoratorInput';
import ErrorMessage from '../../../atoms/Message/Error/ErrorMessage';
import Button from '../../../atoms/Button/Button';
import { merchantWorkflows, workflowAction, WorkflowActionState } from './merchantWorkflows';
import { ErrorType } from '../../../../types/response/ErrorCodes';
import { TableRowContent } from '../../../../types/Layout/TableRowContent';
import {
    getMerchantKey,
    getMerchantStatus,
    hasMerchantName,
    Merchant,
    MerchantStatus,
    merchantStatuses,
    MerchantWorkflow,
} from '../../../../types/onboarding/Merchant';
import { useOnboardingStore } from '../../../../store/admin/onboarding/onboardingStore';
import { useAppState } from '../../../../store/appstate';
import { listMerchants, setMerchantStatus } from '../../../../api/onboarding/onboarding';
import { openMerchantWorkflow } from '../../../../services/helpers/openMerchantWorkflow';
import {
    formatOrganizationNumber,
    normalizeOrganizationNumber,
} from '../../../../services/helpers/organizationNumber';

interface MerchantsSearchResultsProps {
    theme: DefaultTheme;
    organizationId: string;
    /** Write on all stores of the organization: allows opening the KYC and bank account workflows for a merchant. */
    hasStoreWrite?: boolean;
    /** Write on the organization: lets internal users edit the price list of a merchant. */
    hasOrganizationWrite?: boolean;
}

/** Shows the price list of each merchant. Org-scoped LaunchDarkly flag. */
const MERCHANT_PRICES_FLAG = 'TURBO-20-merchant-prices';

type MerchantRow = Merchant & TableRowContent;

const ALL_STATUSES = 'All';
type StatusFilter = MerchantStatus | typeof ALL_STATUSES;
const statusFilters: StatusFilter[] = [ALL_STATUSES, ...merchantStatuses];

/** Search, status chips and counter are only shown when there are more merchants than this. */
const MIN_MERCHANTS_FOR_FILTERING = 3;

const toRow = (merchant: Merchant): MerchantRow => ({
    ...merchant,
    isExpanded: false,
    isRemoved: false,
    isAdded: false,
    isChanged: false,
    isCrossedOver: false,
});

const matchesSearch = (merchant: Merchant, search: string): boolean => {
    const term = search.trim().toLowerCase();

    if (!term) return true;

    return (
        (merchant.name ?? '').toLowerCase().includes(term) ||
        normalizeOrganizationNumber(merchant.organizationNumber).includes(
            normalizeOrganizationNumber(term)
        )
    );
};

const matchesStatus = (merchant: Merchant, statusFilter: StatusFilter): boolean =>
    statusFilter === ALL_STATUSES || getMerchantStatus(merchant) === statusFilter;

const MerchantsSearchResults: React.FC<MerchantsSearchResultsProps> = ({
    theme,
    organizationId,
    hasStoreWrite = false,
    hasOrganizationWrite = false,
}: MerchantsSearchResultsProps) => {
    const {
        merchants,
        isLoadingMerchants,
        merchantsError,
        updatingMerchantKey,
        updateMerchantError,
        updateMerchantStarted,
        updateMerchantSucceeded,
        updateMerchantFailed,
        startingWorkflow,
        openedWorkflow,
        workflowError,
        startWorkflowStarted,
        startWorkflowSucceeded,
        startWorkflowFailed,
        clearOpenedWorkflow,
        listMerchantsSucceeded,
        listMerchantsFailed,
        isSavingPrices,
        isSyncingPrices,
        clearPrices,
    } = useOnboardingStore();
    // Approving is a Walley-internal action: only users in the internal organization see the button.
    const isInternal = useAppState<boolean>(s => s.session.user?.isInternal ?? false);
    const [search, setSearch] = useState('');
    const isPricesEnabled = useIsFlagEnabledForOrg(MERCHANT_PRICES_FLAG);
    // Merchants may view their price list; only internal Merchant Services with organization write edit it.
    const canEditPrices = isInternal && hasOrganizationWrite;
    const [pricesMerchant, setPricesMerchant] = useState<Merchant>();

    const hidePrices = () => {
        if (isSavingPrices || isSyncingPrices) return;

        clearPrices();
        setPricesMerchant(undefined);
    };

    const approve = (merchant: Merchant) =>
        setMerchantStatus(
            organizationId,
            merchant,
            MerchantStatus.Approved,
            updateMerchantStarted,
            updateMerchantSucceeded,
            updateMerchantFailed
        );

    const startWorkflow = (merchant: Merchant, workflow: MerchantWorkflow) =>
        openMerchantWorkflow(
            organizationId,
            merchant,
            workflow,
            startWorkflowStarted,
            startWorkflowSucceeded,
            startWorkflowFailed
        );

    // The Bits webhook may have updated statuses and company data while the workflow tab was open, so the list is
    // re-fetched when the user comes back to this tab. The "started" callback is skipped to keep the table in place.
    useEffect(() => {
        if (!openedWorkflow) return undefined;

        const onFocus = () => {
            clearOpenedWorkflow();
            listMerchants(
                organizationId,
                () => undefined,
                listMerchantsSucceeded,
                listMerchantsFailed
            );
        };

        window.addEventListener('focus', onFocus);

        return () => window.removeEventListener('focus', onFocus);
    }, [
        openedWorkflow,
        organizationId,
        clearOpenedWorkflow,
        listMerchantsSucceeded,
        listMerchantsFailed,
    ]);

    const [statusFilter, setStatusFilter] = useState<StatusFilter>(ALL_STATUSES);

    const workflowIcon = (workflow: MerchantWorkflow) =>
        workflow === MerchantWorkflow.BankAccount ? (
            <BankLineIcon size={theme.icon.size.small} />
        ) : (
            <ShieldCheckLineIcon size={theme.icon.size.small} />
        );

    /** Icon of a workflow action: the button text is the same in every state, so the icon tells how far the application has come. */
    const actionIcon = (workflow: MerchantWorkflow, state: WorkflowActionState) => {
        switch (state) {
            case WorkflowActionState.Continue:
                return <ArrowRightLineIcon size={theme.icon.size.small} />;
            case WorkflowActionState.InReview:
                return <TimeLineIcon size={theme.icon.size.small} />;
            case WorkflowActionState.Declined:
                return <CloseLineIcon size={theme.icon.size.small} />;
            case WorkflowActionState.Done:
                return <CheckLineIcon size={theme.icon.size.small} />;
            default:
                return workflowIcon(workflow);
        }
    };

    const renderWorkflowButton = (merchant: Merchant, workflow: MerchantWorkflow) => {
        if (!hasStoreWrite) return null;

        const action = workflowAction(merchant, workflow);

        // Merchants added with an override have no Bits applications and never show a workflow state.
        if (!action) return null;

        // The application awaits review, was decided or is approved: show where it stands, nothing to open.
        if (!action.opens)
            return (
                <WorkflowButton
                    key={workflow}
                    type="button"
                    disabled
                    data-testid={action.testId}
                    data-workflow-state={action.state}
                >
                    {actionIcon(workflow, action.state)}
                    {action.label}
                </WorkflowButton>
            );

        const isStarting =
            startingWorkflow?.workflow === workflow &&
            startingWorkflow.merchantKey === getMerchantKey(merchant);

        return (
            <WorkflowButton
                key={workflow}
                type="button"
                data-testid={action.testId}
                data-workflow-state={action.state}
                disabled={startingWorkflow !== undefined}
                onClick={() => startWorkflow(merchant, workflow)}
            >
                {!isStarting && actionIcon(workflow, action.state)}
                {isStarting ? 'Opening…' : action.label}
            </WorkflowButton>
        );
    };

    const renderApproveButton = (merchant: Merchant) => {
        if (!isInternal || getMerchantStatus(merchant) !== MerchantStatus.Pending) return null;

        const key = getMerchantKey(merchant);
        const isApproving = updatingMerchantKey === key;

        return (
            <Button
                type="button"
                data-testid={`approve-merchant-${key}`}
                disabled={updatingMerchantKey !== undefined}
                onClick={() => approve(merchant)}
            >
                {isApproving ? 'Approving…' : 'Approve'}
            </Button>
        );
    };

    const renderPricesButton = (merchant: Merchant) => {
        if (!isPricesEnabled) return null;

        const key = getMerchantKey(merchant);

        return (
            <WorkflowButton
                type="button"
                data-testid={`prices-merchant-${key}`}
                onClick={() => setPricesMerchant(merchant)}
            >
                <PriceTag2LineIcon size={theme.icon.size.small} />
                Prices
            </WorkflowButton>
        );
    };

    const statusColor = (status: MerchantStatus): string => {
        switch (status) {
            case MerchantStatus.Approved:
                return theme.colors.success;
            case MerchantStatus.Pending:
                return theme.colors.status.notActivated;
            default:
                return theme.colors.status.default;
        }
    };

    const showing = merchants
        .filter(
            merchant => matchesSearch(merchant, search) && matchesStatus(merchant, statusFilter)
        )
        .map(toRow);

    const columns: ColumnDef<MerchantRow>[] = [
        {
            id: 'countryCode',
            header: 'Country',
            cell: (props: { row: { original: MerchantRow } }) => {
                const countryCode = props.row.original.countryCode?.toUpperCase();

                return (
                    <Flex gap="0.8rem" alignItems="center">
                        <FlagIcon countryCode={countryCode} />
                        <span>{countryCode}</span>
                    </Flex>
                );
            },
        },
        {
            id: 'organizationNumber',
            header: 'Org number',
            cell: (props: { row: { original: MerchantRow } }) => (
                <OrganizationNumber>
                    {formatOrganizationNumber(
                        props.row.original.countryCode,
                        props.row.original.organizationNumber
                    )}
                </OrganizationNumber>
            ),
        },
        {
            id: 'name',
            header: 'Name',
            cell: (props: { row: { original: MerchantRow } }) => {
                const merchant = props.row.original;

                return hasMerchantName(merchant) ? (
                    <MerchantName>{merchant.name}</MerchantName>
                ) : (
                    <Flex column data-testid="merchant-name-placeholder">
                        <PlaceholderTitle>Set by KYC</PlaceholderTitle>
                        <PlaceholderHint>Not available until KYC is complete</PlaceholderHint>
                    </Flex>
                );
            },
        },
        {
            id: 'status',
            header: 'Status',
            cell: (props: { row: { original: MerchantRow } }) => {
                const status = getMerchantStatus(props.row.original);

                return <Pill color={statusColor(status)}>{status}</Pill>;
            },
        },
    ];

    if (isInternal || hasStoreWrite || isPricesEnabled)
        columns.push({
            id: 'actions',
            header: '',
            enableSorting: false,
            cell: (props: { row: { original: MerchantRow } }) => {
                const merchant = props.row.original;
                const workflowButtons = merchantWorkflows
                    .map(workflow => renderWorkflowButton(merchant, workflow))
                    .filter(button => button !== null);
                const approveButton = renderApproveButton(merchant);
                const pricesButton = renderPricesButton(merchant);

                if (workflowButtons.length === 0 && !approveButton && !pricesButton) return null;

                return (
                    <ActionCell>
                        {workflowButtons}
                        {pricesButton}
                        {approveButton}
                    </ActionCell>
                );
            },
        });

    if (isLoadingMerchants) return <Spinner loading={isLoadingMerchants} />;

    if (merchantsError)
        return <ErrorMessage error={merchantsError} errorHeader={ErrorType.LoadMerchants} />;

    if (merchants.length === 0)
        return (
            <EmptyState
                title="No merchants yet"
                icon={
                    <BuildingLineIcon size={theme.icon.size.xlarge} color={theme.colors.accent3} />
                }
            >
                <div>This organization does not have any merchants yet.</div>
            </EmptyState>
        );

    const showFilter = merchants.length > MIN_MERCHANTS_FOR_FILTERING;

    return (
        <>
            {updateMerchantError && (
                <ErrorWrapper data-testid="approve-merchant-error">
                    <ErrorMessage
                        error={updateMerchantError}
                        errorHeader={ErrorType.ApproveMerchant}
                    />
                </ErrorWrapper>
            )}

            {workflowError && (
                <ErrorWrapper data-testid={workflowError.testId}>
                    <ErrorMessage
                        error={workflowError.error}
                        errorHeader={workflowError.errorType}
                    />
                </ErrorWrapper>
            )}

            {openedWorkflow && (
                <InfoWrapper data-testid="workflow-opened">
                    {openedWorkflow.label} for {openedWorkflow.merchantName} opened in a new tab.
                    The list refreshes when you come back.
                </InfoWrapper>
            )}

            {showFilter && (
                <Toolbar>
                    <SearchWrapper>
                        <DecoratorInput
                            dataTestId="filter-merchants"
                            type="text"
                            name="filterMerchants"
                            id="filter-merchants"
                            onChange={e => setSearch(e.target.value)}
                            value={search}
                            placeholder="Search name or org number"
                            decorator={<SearchLineIcon size="17" />}
                        />
                    </SearchWrapper>
                    <Chips role="group" aria-label="Filter merchants by status">
                        {statusFilters.map(filter => (
                            <Chip
                                key={filter}
                                type="button"
                                $active={statusFilter === filter}
                                aria-pressed={statusFilter === filter}
                                data-testid={`merchant-status-filter-${filter.toLowerCase()}`}
                                onClick={() => setStatusFilter(filter)}
                            >
                                {filter}
                            </Chip>
                        ))}
                    </Chips>
                    <Counter data-testid="merchants-counter">
                        {showing.length} of {merchants.length}{' '}
                        {merchants.length === 1 ? 'merchant' : 'merchants'}
                    </Counter>
                </Toolbar>
            )}

            <Card>
                <CardBody>
                    {showing.length === 0 ? (
                        <NoMatch data-testid="merchants-no-match">
                            No merchants match your search.
                        </NoMatch>
                    ) : (
                        <Table<MerchantRow> data={showing} columns={columns} />
                    )}
                </CardBody>
            </Card>

            {pricesMerchant && (
                <Modal large isShown dataTestId="merchant-prices-modal" hide={hidePrices}>
                    <MerchantPricesModal
                        organizationId={organizationId}
                        merchant={pricesMerchant}
                        canEdit={canEditPrices}
                        isInternal={isInternal}
                        hide={hidePrices}
                    />
                </Modal>
            )}
        </>
    );
};

const ErrorWrapper = styled.div`
    margin-bottom: 1rem;
`;

const InfoWrapper = styled.div`
    margin-bottom: 1rem;
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
`;

const ActionCell = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
`;

/** A workflow action: the button itself is the flex container so the icon sits centered next to the label. */
const WorkflowButton = styled(Button)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    padding: 0 1rem;

    /* HK Grotesk caps sit above the line-box center, so a centered icon lands low; lift it to the cap center. */
    svg {
        flex-shrink: 0;
        margin-top: -0.2rem;
    }
`;

const Toolbar = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
`;

const SearchWrapper = styled.div`
    flex: 1 1 24rem;
    max-width: 32rem;
    background: ${props => props.theme.colors.light};
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
`;

const Chip = styled.button<{ $active: boolean }>`
    cursor: pointer;
    border-radius: 5rem;
    padding: 0.5rem 1.4rem;
    font-size: ${props => props.theme.text.size.small};
    font-family: ${props =>
        props.$active ? props.theme.text.font.medium : props.theme.text.font.regular};
    border: 0.1rem solid
        ${props => (props.$active ? props.theme.colors.primary : props.theme.colors.border)};
    background: ${props => (props.$active ? props.theme.colors.accent : 'transparent')};
    color: ${props =>
        props.$active ? props.theme.colors.primary : props.theme.colors.text.primary};

    &:hover {
        border-color: ${props => props.theme.colors.primary};
    }

    &:focus-visible {
        outline: 0.2rem solid ${props => props.theme.colors.primary};
        outline-offset: 0.1rem;
    }
`;

const Counter = styled.div`
    margin-left: auto;
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
    white-space: nowrap;
`;

const OrganizationNumber = styled.span`
    white-space: nowrap;
`;

const MerchantName = styled.span`
    font-family: ${props => props.theme.text.font.medium};
`;

const PlaceholderTitle = styled.span`
    font-style: italic;
    color: ${props => props.theme.colors.text.secondary};
`;

const PlaceholderHint = styled.span`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.subtleSecondary};
`;

const NoMatch = styled.div`
    padding: 2rem;
    text-align: center;
    color: ${props => props.theme.colors.text.secondary};
`;

export default withTheme(MerchantsSearchResults) as React.ComponentType<
    Omit<MerchantsSearchResultsProps, 'theme'>
>;
